import type { Range } from "@tiptap/core";
import type { Mark, MarkType, Node } from "@tiptap/pm/model";

/** Marks a comment must not cut in half: on paper a half-covered link can only be written as two links. */
export const INLINE_OBJECT_MARKS = ["link", "file", "fragment-link", "code"];

const commentOf = (node: Node, commentType: MarkType): Mark => commentType.isInSet(node.marks) ?? null;

const runsOfMark = (block: Node, start: number, mark: Mark): Range[] => {
	const runs: Range[] = [];
	let offset = 0;

	block.forEach((child) => {
		const from = start + offset;
		offset += child.nodeSize;
		if (!mark.isInSet(child.marks)) return;

		const last = runs[runs.length - 1];
		if (last?.to === from) last.to = from + child.nodeSize;
		else runs.push({ from, to: from + child.nodeSize });
	});

	return runs;
};

export const expandRangeToWholeObjects = (doc: Node, range: Range): Range => {
	let { from, to } = range;

	doc.nodesBetween(range.from, range.to, (node, pos) => {
		if (!node.isText) return;
		const resolved = doc.resolve(pos);

		for (const mark of node.marks) {
			if (!INLINE_OBJECT_MARKS.includes(mark.type.name)) continue;

			for (const run of runsOfMark(resolved.parent, resolved.start(), mark)) {
				if (run.to <= pos || run.from >= pos + node.nodeSize) continue;
				from = Math.min(from, run.from);
				to = Math.max(to, run.to);
			}
		}
	});

	return { from, to };
};

const hasForeignComment = (doc: Node, range: Range, mark: Mark, commentType: MarkType) => {
	let found = false;

	doc.nodesBetween(range.from, range.to, (node) => {
		if (!node.isText) return;
		const comment = commentOf(node, commentType);
		if (comment && comment.attrs.id !== mark.attrs.id) found = true;
	});

	return found;
};

/**
 * Comments that cut an inline object in half, with the range they have to grow to. `range` narrows the
 * search to the blocks an edit touched. A comment whose growth would run into another comment is left alone.
 */
export const findCommentRepairs = (
	doc: Node,
	commentType: MarkType,
	range?: Range | null,
): { mark: Mark; from: number; to: number }[] => {
	const repairs: { mark: Mark; from: number; to: number }[] = [];
	const from = Math.max(range?.from ?? 0, 0);
	const to = Math.min(range?.to ?? doc.content.size, doc.content.size);
	if (from > to) return repairs;

	doc.nodesBetween(from, to, (block, blockPos) => {
		if (!block.isTextblock) return;
		const start = blockPos + 1;
		const comments = new Map<string, Mark>();

		block.forEach((child) => {
			const comment = commentOf(child, commentType);
			if (comment) comments.set(comment.attrs.id, comment);
		});

		for (const mark of comments.values()) {
			for (const run of runsOfMark(block, start, mark)) {
				const grown = expandRangeToWholeObjects(doc, run);
				if (grown.from === run.from && grown.to === run.to) continue;
				if (hasForeignComment(doc, grown, mark, commentType)) continue;

				repairs.push({ mark, from: grown.from, to: grown.to });
			}
		}
	});

	return repairs;
};
